import { Component, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil, withLatestFrom } from 'rxjs/operators';

import { AppState } from 'src/app/app.state';
import { Card } from 'src/app/core/api/responses/payment-portal-login';
import { selectAppConfig } from 'src/app/core/application-config/application-config.selectors';
import { Details } from 'src/app/core/user/user.models';
import { selectUser } from 'src/app/core/user/user.selectors';
import { UserState } from 'src/app/core/user/user.state';
import { getPrimary } from 'src/app/core/user/user.utils';
import { validString } from 'src/app/shared/utilities/types.utils';
import { selectMerchantId } from '../../../core/client-configuration/client-configuration.selectors';
import { LocaleService } from '../../../core/locale/locale.service';
import { dayjs } from '../../../dayjs.lib';
import { parseFilenameForAltText } from '../../../shared/utilities/string.utils';

@Component({
	selector: 'ahp-summary-heading',
	templateUrl: './summary-heading.component.html',
	styleUrls: ['./summary-heading.component.scss'],
})
export class SummaryHeadingComponent implements OnDestroy {
	/**
	 * Our customer information
	 */
	customer: Details;
	/**
	 * The customer's order id
	 */
	orderNumber: string;
	/**
	 * The primary card on file
	 */
	primaryPayment: Card;
	/**
	 * Timestamp for the purchase of this order
	 */
	purchaseTimestamp: string;
	/**
	 * Holds the alternate payment type
	 */
	alternatePayment: string;
	/**
	 * logo path built from appConfig
	 */
	logoPath: string;
	/**
	 * logo path built from appConfig
	 */
	logoAltTag: string;
	/**
	 * For unsubscribing
	 */
	private _destroy$ = new Subject<void>();

	/**
	 * Our constructor
	 * @param _store$ The store
	 * @param _locale the locale service
	 */
	constructor(private _store$: Store<AppState>, private _locale: LocaleService) {
		this._store$.pipe(select(selectUser), takeUntil(this._destroy$)).subscribe((userState: UserState) => {
			const { user } = userState;

			this.alternatePayment = user?.currentPaymentType;
			this.orderNumber = user?.orderNumber;
			// If Bookings Only, will only have name details for one user
			this.customer = user?.details?.find(getPrimary) ?? user?.details?.[0];
			this.primaryPayment = user?.cards?.find(getPrimary);
			this.purchaseTimestamp = user?.purchaseDate
				? dayjs(user?.purchaseDate, 'YYYY-MM-DD h:mm A').format('L LT')
				: null;
		});

		this._store$
			.pipe(
				select(selectAppConfig(['assetsPath', 'logo'])),
				withLatestFrom(this._store$.select(selectMerchantId)),
				takeUntil(this._destroy$)
			)
			.subscribe(([appConfig, merchantId]) => {
				const logoAltLocale = `accessibility.logoalttext_${merchantId}`;
				const { assetsPath, logo } = appConfig;
				if (validString(assetsPath) && validString(logo)) {
					this.logoPath = assetsPath.concat(logo);
					this.logoAltTag = this._locale.has(logoAltLocale)
						? this._locale.get(logoAltLocale)
						: parseFilenameForAltText(logo);
				}
			});
	}

	ngOnDestroy(): void {
		this._destroy$.next();
		this._destroy$.complete();
	}
}
